import { Tooltip } from '@civilgrid/ui/components';
import { useEffect } from 'react';
import { useMap } from 'react-map-gl/mapbox';
import { useSnapshot } from 'valtio';
import Button from '~/components2/forms/Button';
import { mapState } from '../../Map';

const BEARING_INCREMENT = 10;
const PAN_VALUE = 100;

interface Props {
  visible?: boolean;
}

const ROTATE_KEYCODES = new Set<string>(['KeyA', 'KeyD']);

export default function Rotation({ visible = true }: Props) {
  const { current: map } = useMap();
  const { viewState } = useSnapshot(mapState);

  const resetBearing = () => {
    if (!map) return;
    map.easeTo({ bearing: 0 });
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    if (map) {
      map.getCanvas()?.addEventListener('keydown', onKeyDown);
    }

    return () => {
      if (map) {
        map.getCanvas()?.removeEventListener('keydown', onKeyDown);
      }
    };
  }, []);

  const onKeyDown = (e: KeyboardEvent) => {
    // A modified or composing key belongs to the browser or the IME, so Cmd+D still bookmarks.
    if (e.metaKey || e.ctrlKey || e.altKey || e.isComposing) return;

    const { code } = e;

    // Only the keys this widget acts on are cancelled; the rest keep their default, so Tab and
    // Shift+Tab still move focus off the canvas.
    if (code.toLowerCase().startsWith('arrow')) {
      e.preventDefault();
      panMap(e);
    } else if (ROTATE_KEYCODES.has(code)) {
      e.preventDefault();
      rotateMap(e);
    }
  };

  const panMap = (e: KeyboardEvent) => {
    if (!map) return;
    const { code: key } = e;
    let dx = 0;
    let dy = 0;

    switch (key) {
      case 'ArrowUp':
        dy = -PAN_VALUE;
        break;
      case 'ArrowDown':
        dy = PAN_VALUE;
        break;
      case 'ArrowLeft':
        dx = -PAN_VALUE;
        break;
      case 'ArrowRight':
        dx = PAN_VALUE;
        break;
    }

    map.panBy([dx, dy]);
  };

  const rotateMap = (e: KeyboardEvent) => {
    if (!map) return;
    const { code } = e;
    const bearing = map.getBearing();

    let inc = 0;

    switch (code) {
      case 'KeyA':
        inc = BEARING_INCREMENT;
        break;
      case 'KeyD':
        inc = -BEARING_INCREMENT;
        break;
    }

    mapState.viewState.bearing = viewState.bearing + inc;
    map.easeTo({ bearing: bearing + inc });
  };

  if (!visible) {
    return null;
  }

  return (
    <div className="z-10">
      <Tooltip asChild text="Use `A` and `D` to rotate map" side="right" sideOffset={10}>
        <Button
          alternate
          size="icon"
          onClick={resetBearing}
          className="min-w-auto!"
          aria-label="Reset map rotation"
          data-testid="map-rotation-reset"
        >
          <div
            style={{
              transform: `rotate(${map ? `${-1 * map.getBearing()}deg` : '0deg'})`,
            }}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 15 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="fill-primary hover:fill-secondary"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M0.852087 7.68843C0.852087 4.01693 3.82843 1.04059 7.49992 1.04059C11.1714 1.04059 14.1478 4.01693 14.1478 7.68843C14.1478 11.3599 11.1714 14.3363 7.49992 14.3363C3.82843 14.3363 0.852087 11.3599 0.852087 7.68843ZM7.49992 2.04059C4.38071 2.04059 1.85209 4.56922 1.85209 7.68843C1.85209 10.8076 4.38071 13.3363 7.49992 13.3363C10.6191 13.3363 13.1478 10.8076 13.1478 7.68843C13.1478 4.56922 10.6191 2.04059 7.49992 2.04059Z"
              />
              <path d="M7.49988 2.61775L9.23817 7.68834H5.76159L7.49988 2.61775Z" />
              <path
                className="stroke-primary hover:stroke-secondary"
                d="M8.88842 7.93827L7.50012 11.988L6.11182 7.93827L8.88842 7.93827Z"
                fill="white"
                strokeWidth="0.5"
              />
            </svg>
          </div>
        </Button>
      </Tooltip>
    </div>
  );
}
